// ── Report detail / viewer ("/reports/:report_id") ─────────────────────────
// The full report record: an identity header (title + audience + status + cohort)
// over the content (rendered SAFELY), the review affordance, the real HTML
// attachment download, and the SA-only regenerate. The report is resolved from
// the server-visibility-filtered LIST by id; D-3 re-checks the readable filter so
// a non-staff role can never open a confidential row by guessing its id.
//
// ── PARTICIPANT GENERATED-REPORT EXCLUSION (FR-REPORT-001, release-blocking) ──
// The route manifest keeps /reports/:id at reportViewRoles (participant excluded →
// generic-not-found). THIS page ALSO fail-closes (canAccessReportsModule): a
// participant who somehow reached the component renders ONLY permission-denied
// with NO report, and the report read NEVER fires for a participant (`enabled`
// gates the query). A participant must not learn a report even EXISTS.
//
// ── SANITIZED HTML (blueprint §6 gate #7) ────────────────────────────────────
// Content is plain text + arrays; ReportContentSection renders with React's
// default escaping — NEVER dangerouslySetInnerHTML. The id is a STRING — passed
// VERBATIM, never Number().

import { Badge, DetailSurface, Module, ModuleBody, ModuleHead } from "@vjal/ui";
import { useMemo } from "react";
import { Link } from "react-router-dom";

import { HonestState, useHonestQuery } from "../../components/honest-state";
import { getCoachingServices } from "../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../hooks/coaching-query-keys";
import { reportTypeLabels } from "../../domain/report-taxonomy";
import { liveRecordIdFromPath } from "../product-live-rows";
import type { CoachingArtifactDto, ListResponse } from "../../services/coaching-dtos";
import type { ProductScreenContext } from "../product-route-contracts";
import {
  canAccessReportsModule,
  filterReadableReportDtos,
  isReportReady,
  isValidReportId,
  orDash,
  reportStatusLabel,
  reportStatusTone
} from "./reports-helpers";
import { ReportContentSection } from "./report-detail/report-content-section";
import { ReportReviewSection } from "./report-detail/report-review-section";
import { ReportDownloadSection } from "./report-detail/report-download-section";
import { ReportRegenerateSection } from "./report-detail/report-regenerate-section";

const services = getCoachingServices();

export function ReportDetailPage({ role, routePath }: ProductScreenContext) {
  const reportId = liveRecordIdFromPath(routePath, "reports");
  const validId = isValidReportId(reportId);
  const moduleDenied = !canAccessReportsModule(role);

  // Resolve from the server-visibility-filtered list; the read NEVER fires for an
  // excluded role or an invalid id.
  const query = useHonestQuery<ListResponse<CoachingArtifactDto>>(
    coachingQueryKeys.list("reports.list"),
    () => services.reports.list(),
    { enabled: validId && !moduleDenied }
  );

  // D-3 belt-and-suspenders: only consider rows this role may read, THEN find by
  // id — so a non-staff role can never open a confidential report by guessing.
  const report = useMemo(() => {
    const readable = filterReadableReportDtos(role, query.data?.items ?? []);
    return readable.find((row) => row.id === reportId) ?? null;
  }, [query.data, role, reportId]);

  // ── Fail closed: a denied role gets permission-denied, NO report. ───────────
  if (moduleDenied) {
    return (
      <section aria-label="Report detail" className="col-span-12" data-testid="report-detail-page">
        <div className="rounded-xl border border-line bg-white">
          <HonestState
            posture="permission-denied"
            title="You don't have access to this report"
            message="The reports area is for coaching staff and assigned leads. Your own coaching summary is available on your dashboard."
          />
        </div>
      </section>
    );
  }

  // ── Bad/absent id, a non-ready posture, or an id not in the readable set ────
  if (!validId || query.posture !== null || !report) {
    const posture = !validId ? "empty" : query.posture ?? (query.data ? "empty" : "loading");
    const notFound = posture === "empty";
    return (
      <section aria-label="Report detail" className="col-span-12" data-testid="report-detail-page">
        <div className="rounded-xl border border-line bg-white">
          <HonestState
            posture={posture}
            gate={query.gate}
            reference={query.reference}
            onRetry={query.retry}
            title={notFound ? "Report not found" : undefined}
            message={
              notFound
                ? "This report doesn't exist, or it isn't one you're authorized to see. It may not have been generated yet."
                : undefined
            }
            canCreate={notFound}
            createAction={
              notFound ? (
                <Link to="/reports" className="text-link no-underline hover:underline">
                  Back to reports
                </Link>
              ) : undefined
            }
          />
        </div>
      </section>
    );
  }

  const ready = isReportReady(report.status);

  return (
    <section aria-label="Report detail" className="col-span-12 flex flex-col gap-4" data-testid="report-detail-page">
      {/* F3: real @vjal/ui DetailSurface for Detail-1 report-detail. */}
      <DetailSurface
        title={
          <span className="flex flex-wrap items-center gap-3">
            {orDash(report.title ?? report.session_title)}
            <span data-report-type={report.report_type ?? "team_admin"}>
              <Badge variant="info">{report.report_type ? reportTypeLabels[report.report_type] : "Team / company"}</Badge>
            </span>
            <Badge variant={reportStatusTone(report.status) === "danger" ? "danger" : reportStatusTone(report.status)}>
              {reportStatusLabel(report.status)}
            </Badge>
          </span>
        }
        fields={[
          {
            id: "back",
            label: "Directory",
            value: (
              <Link to="/reports" className="text-link no-underline hover:underline">
                ← All reports
              </Link>
            ),
          },
          {
            id: "cohort",
            label: "Cohort",
            value: orDash(report.cohort_name),
          },
          {
            id: "session",
            label: "Session",
            value: orDash(report.session_title),
          },
        ]}
        titleExactName="Report title">
        <div className="mt-4 grid grid-cols-12 gap-4">
          {/* Content shown only when ready; otherwise the status section carries the
              honest not-ready / failed posture (never a fabricated body). */}
          {ready ? <ReportContentSection report={report} /> : <ReportStatusBody report={report} />}
          <ReportReviewSection report={report} role={role} />
          <ReportDownloadSection report={report} role={role} />
          <ReportRegenerateSection role={role} sessionId={report.session_id} />
        </div>
      </DetailSurface>
    </section>
  );
}

/** Not-ready / failed report → honest status posture (no fabricated content). */
function ReportStatusBody({ report }: { report: CoachingArtifactDto }) {
  const failed = reportStatusTone(report.status) === "danger";
  return (
    <Module span={8}>
      <ModuleHead title="Report" subtitle="This report isn't ready to read yet." />
      <ModuleBody data-testid="report-content">
        <div data-honest-posture={failed ? "error" : "unavailable"} className="py-2">
          {failed ? (
            <HonestState
              posture="error"
              title="Report generation failed"
              message="This report didn't generate successfully. A coaching admin can look into why. No content is available."
            />
          ) : (
            <HonestState
              posture="unavailable"
              title="Report not ready yet"
              message="This report is still being generated. Its content will appear here once it's ready."
            />
          )}
        </div>
      </ModuleBody>
    </Module>
  );
}
